import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const CareerSuccess = () => {
    const { currentUser } = useAuth();
    const [agreed, setAgreed] = useState(false);
    const [processing, setProcessing] = useState(false);

    const handlePurchase = () => {
        if (!agreed) {
            alert('Please agree to the terms and conditions');
            return;
        }
        
        setProcessing(true);
        // Redirect to payment gateway or show payment modal
        window.open('https://pay.yoco.com/gradpath-sa-pty-ltd', '_blank');
        setProcessing(false);
    };

    const features = [
        {
            icon: "fas fa-file-alt",
            title: "CV Optimization",
            description: "Professional CV review and optimization for each job application. We tailor your CV to match specific job requirements, highlight relevant skills, and improve keyword optimization for ATS systems.",
            color: "from-blue-500 to-blue-700"
        },
        {
            icon: "fas fa-paper-plane",
            title: "Application Assistance",
            description: "End-to-end application support including cover letter customization, application form filling assistance, and tracking of all your submissions in one place.",
            color: "from-green-500 to-green-700"
        },
        {
            icon: "fas fa-chalkboard-user",
            title: "Interview Preparation",
            description: "Mock interview sessions, common question practice, behavioral interview training, and personalized feedback to boost your confidence.",
            color: "from-purple-500 to-purple-700"
        },
        {
            icon: "fas fa-network-wired",
            title: "Networking Guidance",
            description: "LinkedIn profile optimization, professional networking strategies, and connecting you with industry professionals.",
            color: "from-orange-500 to-orange-700"
        },
        {
            icon: "fas fa-chart-line",
            title: "Career Coaching",
            description: "One-on-one career guidance sessions to help you identify your strengths, set goals, and create a long-term career roadmap.",
            color: "from-red-500 to-red-700"
        },
        {
            icon: "fas fa-clock",
            title: "3-Month Support",
            description: "Continuous support for 3 months including follow-up sessions, additional document reviews, and job search strategies.",
            color: "from-teal-500 to-teal-700"
        }
    ];

    return (
        <div className="container mx-auto px-4 py-8 max-w-6xl">
            {/* Header Section */}
            <div className="text-center mb-12">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-2xl mb-4">
                    <i className="fas fa-rocket text-white text-3xl"></i>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gradpath-dark mb-4">
                    Career Success Program
                </h1>
                <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                    Accelerate your career journey with our comprehensive job search support package
                </p>
                
                {/* Price Tag */}
                <div className="mt-6 inline-flex items-center gap-4 bg-gradient-to-r from-gradpath-green to-gradpath-dark text-white px-8 py-4 rounded-full shadow-lg">
                    <span className="text-2xl font-bold">R600</span>
                    <span className="text-white/80">one-time payment</span>
                    <span className="bg-white/20 px-3 py-1 rounded-full text-sm">3 months access</span>
                </div>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {features.map((feature, index) => (
                    <div key={index} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all group overflow-hidden">
                        <div className={`bg-gradient-to-r ${feature.color} p-4 text-white`}>
                            <i className={`${feature.icon} text-3xl`}></i>
                        </div>
                        <div className="p-5">
                            <h3 className="text-xl font-bold text-gradpath-dark mb-2">{feature.title}</h3>
                            <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* How It Works */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-12">
                <h2 className="text-2xl font-bold text-gradpath-dark mb-6 text-center">How It Works</h2>
                <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center">
                        <div className="w-16 h-16 bg-gradpath-green rounded-full flex items-center justify-center mx-auto mb-3">
                            <span className="text-white text-2xl font-bold">1</span>
                        </div>
                        <h3 className="font-semibold text-gray-800 mb-2">Enroll & Pay</h3>
                        <p className="text-gray-500 text-sm">Complete payment and create your account</p>
                    </div>
                    <div className="text-center">
                        <div className="w-16 h-16 bg-gradpath-green rounded-full flex items-center justify-center mx-auto mb-3">
                            <span className="text-white text-2xl font-bold">2</span>
                        </div>
                        <h3 className="font-semibold text-gray-800 mb-2">Get Matched</h3>
                        <p className="text-gray-500 text-sm">We assign a career coach to guide you</p>
                    </div>
                    <div className="text-center">
                        <div className="w-16 h-16 bg-gradpath-green rounded-full flex items-center justify-center mx-auto mb-3">
                            <span className="text-white text-2xl font-bold">3</span>
                        </div>
                        <h3 className="font-semibold text-gray-800 mb-2">Start Your Journey</h3>
                        <p className="text-gray-500 text-sm">Receive personalized support and start applying</p>
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-xl p-8 text-center text-white mb-12">
                <h2 className="text-2xl md:text-3xl font-bold mb-3">Ready to Invest in Your Career?</h2>
                <p className="text-white/90 mb-6">Join hundreds of graduates who have successfully launched their careers</p>
                
                {!currentUser && (
                    <div className="mb-4 p-3 bg-white/20 rounded-lg inline-block">
                        <i className="fas fa-info-circle mr-2"></i>
                        Please login or create an account before purchasing
                    </div>
                )}
                
                <button
                    onClick={handlePurchase}
                    disabled={!currentUser || processing}
                    className="bg-white text-gradpath-green hover:bg-gray-100 font-bold py-3 px-8 rounded-full shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {processing ? (
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                    ) : (
                        <i className="fas fa-shopping-cart mr-2"></i>
                    )}
                    Enroll Now - R600
                </button>
                
                <p className="text-white/70 text-sm mt-4">
                    Secure payment via Yoco • Instant access after payment
                </p>
            </div>

            {/* Disclaimers */}
            <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-yellow-500">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <i className="fas fa-exclamation-triangle text-yellow-500"></i>
                    Important Information
                </h3>
                
                <div className="space-y-3 text-sm text-gray-600">
                    <div className="flex gap-2">
                        <i className="fas fa-ban text-red-500 mt-0.5"></i>
                        <p><strong className="text-red-600">No Job Guarantee:</strong> The Career Success Program provides tools, guidance, and support to enhance your job search. However, we do not guarantee job placement or employment. Success depends on various factors including your qualifications, interview performance, and market conditions.</p>
                    </div>
                    
                    <div className="flex gap-2">
                        <i className="fas fa-credit-card text-red-500 mt-0.5"></i>
                        <p><strong className="text-red-600">No Refunds:</strong> All payments for the Career Success Program are non-refundable. Once enrolled, you receive immediate access to our services and resources.</p>
                    </div>
                    
                    <div className="flex gap-2">
                        <i className="fas fa-clock text-gray-500 mt-0.5"></i>
                        <p><strong>Program Duration:</strong> Access to services is valid for 3 months from the date of enrollment.</p>
                    </div>
                    
                    <div className="flex gap-2">
                        <i className="fas fa-shield-alt text-gray-500 mt-0.5"></i>
                        <p><strong>Terms Apply:</strong> By enrolling, you agree to our <Link to="/terms" className="text-gradpath-green hover:underline">Terms & Conditions</Link> and <Link to="/privacy" className="text-gradpath-green hover:underline">Privacy Policy</Link>.</p>
                    </div>
                </div>
                
                {/* Terms Agreement Checkbox */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={agreed}
                            onChange={(e) => setAgreed(e.target.checked)}
                            className="mt-0.5 w-4 h-4 text-gradpath-green rounded focus:ring-gradpath-green"
                        />
                        <span className="text-sm text-gray-700">
                            I understand that the Career Success Program offers job search support and guidance, 
                            but does not guarantee employment. I also acknowledge that payments are non-refundable.
                        </span>
                    </label>
                </div>
            </div>
        </div>
    );
};

export default CareerSuccess;